// src/features/pages/admin/roles/RolesTab.tsx
// Custom role builder surface: a table of system (read-only) + custom (editable)
// roles, with create/edit/delete for the tenant's own custom roles.
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, Plus, Pencil, Trash2, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { deleteRole, type RbacCatalog, type Role, type RolesResponse } from '@/services/rbacService';
import { RoleBuilderDialog } from './RoleBuilderDialog';

interface Props {
  catalog: RbacCatalog;
  roles: RolesResponse;
  loading: boolean;
  onReload: () => void;
}

const scopeBadge = (scope: string) => {
  const variant = scope === 'tenant' ? 'purple' : scope === 'team' ? 'teal' : 'neutral';
  return (
    <Badge variant={variant as any} className="capitalize">
      {scope}
    </Badge>
  );
};

const RoleRow: React.FC<{
  role: Role;
  onEdit?: (r: Role) => void;
  onDelete?: (r: Role) => void;
  busy?: boolean;
}> = ({ role, onEdit, onDelete, busy }) => (
  <div className="flex items-center gap-3 py-3 flex-wrap">
    <div className="flex-1 min-w-[180px]">
      <div className="flex items-center gap-2">
        <span className="font-medium text-foreground">{role.label}</span>
        {role.isSystem ? (
          <Badge variant="neutral" className="gap-1">
            <ShieldCheck className="w-3 h-3" /> System
          </Badge>
        ) : (
          <Badge variant="orange" className="gap-1">
            <Sparkles className="w-3 h-3" /> Custom
          </Badge>
        )}
      </div>
      <div className="text-xs text-muted-foreground font-mono mt-0.5">{role.key}</div>
      {role.description && (
        <div className="text-sm text-muted-foreground mt-0.5">{role.description}</div>
      )}
    </div>
    {scopeBadge(role.scope)}
    <span className="text-sm text-muted-foreground w-[92px] text-right">
      {role.permissions.length} perm{role.permissions.length === 1 ? '' : 's'}
    </span>
    <div className="flex items-center gap-1.5 w-[150px] justify-end">
      {!role.isSystem && onEdit && (
        <Button variant="outline" size="sm" onClick={() => onEdit(role)} disabled={busy}>
          <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
        </Button>
      )}
      {!role.isSystem && onDelete && (
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-danger"
          onClick={() => onDelete(role)}
          disabled={busy}
          aria-label={`Delete ${role.label}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        </Button>
      )}
    </div>
  </div>
);

export const RolesTab: React.FC<Props> = ({ catalog, roles, loading, onReload }) => {
  const confirm = useConfirm();
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setBuilderOpen(true);
  };
  const openEdit = (r: Role) => {
    setEditing(r);
    setBuilderOpen(true);
  };

  const onDelete = async (r: Role) => {
    const ok = await confirm({
      title: 'Delete role',
      message: `Delete the custom role “${r.label}”? This can’t be undone.`,
      confirmLabel: 'Delete role',
      danger: true,
    });
    if (!ok) return;
    setDeletingId(r.id);
    try {
      await deleteRole(r.id);
      toast.success(`Role “${r.label}” deleted`);
      onReload();
    } catch (e) {
      // 409 "role in use" and other refusals surface verbatim.
      toast.error(getApiErrorMessage(e, 'Could not delete the role'), { duration: 7000 });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">
          Build roles from your company’s allowed permissions, then assign them to people.
        </p>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-1" /> New role
        </Button>
      </div>

      {loading ? (
        <div className="py-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading roles…
        </div>
      ) : (
        <>
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-sm font-semibold text-foreground mb-1">Custom roles</h3>
              {roles.customRoles.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No custom roles yet. Create one to tailor access for your company.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {roles.customRoles.map((r) => (
                    <RoleRow
                      key={r.id}
                      role={r}
                      onEdit={openEdit}
                      onDelete={onDelete}
                      busy={deletingId === r.id}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <h3 className="text-sm font-semibold text-foreground mb-1">System roles</h3>
              <p className="text-xs text-muted-foreground mb-2">
                Built-in roles you can assign but not edit.
              </p>
              {roles.systemRoles.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No system roles available.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {roles.systemRoles.map((r) => (
                    <RoleRow key={r.id} role={r} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <RoleBuilderDialog
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        catalog={catalog}
        role={editing}
        onSaved={onReload}
      />
    </div>
  );
};

export default RolesTab;
